import anime from 'animejs';
import Flickity from 'flickity';
import 'flickity-imagesloaded';

import { on, off } from '@pwup/core/listener';
import { rect } from '@pwup/dom/node';
import { query, queryAll } from '@pwup/dom/query';

const CAROUSEL_OPTIONS = {
  accessibility: false,
  adaptiveHeight: false,
  cellSelector: '.text-img__slide',
  imagesLoaded: true,
  lazyLoad: 2,
  pageDots: false,
  prevNextButtons: false,
  wrapAround: true,
  selectedAttraction: 0.05,
  friction: 0.6,
};
const LINE_OFFSET = 30;
const LINE_WIDTH = 240;

class TextImageParallax {
  constructor($el, emitter) {
    this.el = $el;
    this.emitter = emitter;
    this.wrap = query('.text-img__wrap', this.el);
    this.title = query('.text-img__title span', this.el);
    this.img = query('.text-img__img', this.el);
    this.decoration = query('.text-img__decoration', this.el);
    this.sliders = Array.from( queryAll('.--slider', this.el) ).map(el => new Slider(el));

    // get first master slider
    const master = this.sliders.find(slider => slider.slave === false);

    // if there is at least one master slider in this block
    if( master ) {
      // filter slave sliders and assign them the master slider as controller
      this.sliders
        .filter(slider => slider.slave === true)
        .forEach(slider => slider.master = master);
    }

    this._options = JSON.parse(this.el.dataset.textImageParallax);

    if( this.decoration ) this._draw();
  }

  destroy() {
    if( this.sliders ) this.sliders.forEach(slider => slider.destroy());

    this.el = null;
    this.emitter = null;
    this.wrap = null;
    this.title = null;
    this.img = null;
    this.decoration = null;
    this.sliders = null;

    this._options = null;
  }
  start() {
    this.sliders.forEach(slider => slider.start());
  }
  stop() {
    this.sliders.forEach(slider => slider.stop());
  }

  _draw() {
    const wrap_rect = rect(this.wrap);
    const title_rect = rect(this.title);
    const img_rect = rect(this.img);

    if( this._options.alignment === 'left' ) {
      anime.set(this.decoration, {
        top: title_rect.top - wrap_rect.top + title_rect.height * 0.5,
        left: title_rect.left - wrap_rect.left + title_rect.width + LINE_OFFSET,
        right: wrap_rect.right - img_rect.left - LINE_WIDTH,
      });
    }
    else {
      anime.set(this.decoration, {
        top: title_rect.top - wrap_rect.top + title_rect.height * 0.5,
        right: wrap_rect.right - title_rect.right + title_rect.width + LINE_OFFSET,
        left: img_rect.right - wrap_rect.left - LINE_WIDTH,
      });
    }
  }
}

class Slider {
  constructor(el) {
    this.el = el;
    this.carousel = query('.text-img__carousel', this.el);
    this.nextBtn = query('.text-img__arrowNext', this.el);
    this.previousBtn = query('.text-img__arrowPrevious', this.el);

    const options = { ...CAROUSEL_OPTIONS };
    if( this.slave ) options.draggable = false;

    this.flickity = new Flickity(this.carousel, options);

    this._handleNext = this._handleNext.bind(this);
    this._handlePrevious = this._handlePrevious.bind(this);
    this._handleMasterChange = this._handleMasterChange.bind(this);
    this._handleMasterScroll = this._handleMasterScroll.bind(this);
  }

  destroy() {
    if( this.flickity ) this.flickity.destroy();
    if( this.nextBtn ) off(this.nextBtn, 'click', this._handleNext);
    if( this.previousBtn ) off(this.previousBtn, 'click', this._handlePrevious);
    if( this._master ) {
      this._master.flickity.off('change', this._handleMasterChange);
      this._master.flickity.off('scroll', this._handleMasterScroll);
    }

    this.el = null;
    this.carousel = null;
    this.nextBtn = null;
    this.previousBtn = null;
    this.flickity = null;

    this._master = null;
    this._handleNext = null;
    this._handlePrevious = null;
    this._handleMasterChange = null;
    this._handleMasterScroll = null;
  }
  start() {
    if( this.nextBtn ) on(this.nextBtn, 'click', this._handleNext);
    if( this.previousBtn ) on(this.previousBtn, 'click', this._handlePrevious);
  }
  stop() {
    if( this.nextBtn ) off(this.nextBtn, 'click', this._handleNext);
    if( this.previousBtn ) off(this.previousBtn, 'click', this._handlePrevious);
  }

  next() {
    if ( this.flickity ) this.flickity.next();
  }
  previous() {
    if ( this.flickity ) this.flickity.previous();
  }

  _handleNext(event) {
    if( event ) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.next();
  }
  _handlePrevious(event) {
    if( event ) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.previous();
  }

  _handleMasterChange(index) {
    this.flickity.select(index);
  }
  _handleMasterScroll(progress) {
    progress;
    //console.log(progress);
  }

  // getter - setter
  get slave() { return !this.nextBtn && !this.previousBtn; }

  get master() { return this._master; }
  set master(value) {
    if( this._master === value ) return;

    this._master = value;
    this._master.flickity.on('change', this._handleMasterChange);
    //this._master.flickity.on('scroll', this._handleMasterScroll);
  }
}

export default TextImageParallax;
